/**
 * File Viewer 渲染器集合（网络教学 → 文件在线预览）。
 *
 * 只挑选「浏览器里直接预览常见文档」需要的几条链路，避免把 145MB 的全量包打进项目：
 *   - Word       docx / docm / dotx / dotm（自研引擎，Worker 解析）
 *   - Word       doc / dot（老版 OLE 二进制，可选能力，缺失时自动降级提示）
 *   - PPT        pptx / pptm / potx / potm / ppsx / ppmsm
 *   - Excel      xlsx / xls / xltx / xlsm / xlsb / csv
 *   - PDF        pdf
 *   - 文本       txt / md / json / xml / log / 各类源码文件（渲染器自带按需加载高亮）
 *
 * 这些插件以 `renderers` 选项传进 mountViewer，只对「文件预览」这一个实例生效，
 * 不注册成全局自动预设，避免动到后台其它页面。
 *
 * 实现说明（重要）：
 * @file-viewer/* 各个 renderer 只发布现代 ESM（含 `import.meta.url`、顶层 await），
 * 当前项目是 webpack 4 + Vue CLI 4，直接 import 会在解析阶段报
 * 「Module parse failed: Unexpected token」。
 * 所以这里改成**运行时动态加载**：经 webpack 忽略后按原生 ESM 由浏览器直接加载，
 * 既能正常使用，又不会把体积压进业务包；文件预览页面按需触发，不影响其它页面首屏。
 */
const RENDERER_PACKAGES = [
  '@file-viewer/renderer-word',
  '@file-viewer/renderer-pptx',
  '@file-viewer/renderer-spreadsheet',
  '@file-viewer/renderer-pdf',
  '@file-viewer/renderer-text',
]

/** 与 mountViewer 一致的资源根目录（见页面里的 resolveAssetBase） */
let runtimeAssetBase = '/file-viewer/'

/**
 * 让 webpack 不解析该依赖，保留浏览器原生 `import()`。
 * 新版 webpack（5）用 `webpackIgnore`，webpack 4 用魔法注释 `webpackIgnore` 并不识别，
 * 因此这里借助 `new Function` 包裹，彻底绕开打包器静态分析。
 * @param {string} specifier 模块标识
 * @returns {Promise<any>} 模块命名空间
 */
function importRuntimeModule(specifier) {
  const nativeImport = new Function('specifier', 'return import(specifier)')
  return nativeImport(specifier)
}

/**
 * 设置运行时模块解析用的资源根目录。
 * dev（webpack-dev-server）下指向项目 public 目录里的同路径文件；
 * 生产环境由 scripts/copy-file-viewer-runtime.js 拷到 dist/file-viewer/runtime。
 * @param {string} assetBase 形如 `/file-viewer/`
 */
export function setFileViewerRuntimeBase(assetBase) {
  runtimeAssetBase = assetBase || '/file-viewer/'
}

/**
 * 生成一个运行时模块地址：<base>runtime/<key>.js
 * @param {string} key 模块短名
 * @returns {string} 可直接 import 的绝对地址
 */
function runtimeModuleUrl(key) {
  const base = runtimeAssetBase.replace(/\/?$/, '/')
  return new URL(`${base}runtime/${key}.js`, window.location.origin).href
}

/** 动态 import 的去重缓存 */
let presetPromise = null

/** core 加载缓存 */
let runtimePromise = null

/**
 * 运行时总入口：加载 core + 各 renderer，返回可直接交给页面使用的对象。
 * 只加载一次；失败后允许重试（例如资源刚好没部署完）。
 * @param {{assetBase?: string}} [options] 可选的资源根目录
 * @returns {Promise<{mountViewer: Function, setDefaultFileViewerAssetBaseUrl: Function, rendererPreset: object}>}
 */
export function ensureFileViewerRuntime(options = {}) {
  // 允许调用方顺带指定资源根目录，省一次 setFileViewerRuntimeBase
  if (options.assetBase) {
    setFileViewerRuntimeBase(options.assetBase)
  }

  if (!runtimePromise) {
    const load = key => importRuntimeModule(runtimeModuleUrl(key))

    runtimePromise = Promise.all([load('core'), loadFileViewerRendererPreset()])
      .then(([core, rendererPreset]) => {
        if (typeof core.mountViewer !== 'function') {
          throw new Error('文件预览引擎加载失败：未找到 mountViewer')
        }

        return {
          mountViewer: core.mountViewer,
          setDefaultFileViewerAssetBaseUrl: core.setDefaultFileViewerAssetBaseUrl,
          rendererPreset,
        }
      })
      .catch(error => {
        runtimePromise = null
        throw error
      })
  }

  return runtimePromise
}

/**
 * 按需加载并组装 renderer 预设（只加载一次）。
 * @returns {Promise<{id: string, label: string, renderers: any[]}>} 可传给 mountViewer 的 renderers 选项
 */
export function loadFileViewerRendererPreset() {
  if (presetPromise) {
    return presetPromise
  }

  const load = key => importRuntimeModule(runtimeModuleUrl(key))

  presetPromise = Promise.all([
    load('renderer-word'),
    load('renderer-pptx'),
    load('renderer-spreadsheet'),
    load('renderer-pdf'),
    load('renderer-text'),
  ])
    .then(([word, pptx, spreadsheet, pdf, text]) => ({
      id: 'skill-library-document-preview',
      label: '技能库 · 常见文档预览',
      renderers: [
        word.wordRenderer,
        pptx.pptxRenderer,
        spreadsheet.spreadsheetRenderer,
        pdf.pdfRenderer,
        text.textRenderer,
      ].filter(Boolean),
    }))
    .catch(error => {
      // 失败后允许再次重试（例如资源还没部署完）
      presetPromise = null
      throw error
    })

  return presetPromise
}

/** 运行时可加载的包名（供构建脚本与排查页使用） */
export { RENDERER_PACKAGES }

/**
 * 页面顶部「支持预览的格式」提示用的列表（仅提示用，真实能力由上面的渲染器决定）。
 */
export const PREVIEW_ACCEPT_EXTENSIONS = [
  'doc',
  'docx',
  'dot',
  'dotx',
  'docm',
  'pdf',
  'xls',
  'xlsx',
  'xlsm',
  'xlsb',
  'csv',
  'ppt',
  'pptx',
  'ppsx',
  'potx',
  'md',
  'txt',
  'json',
  'xml',
  'log',
  'html',
  'js',
  'ts',
  'py',
  'java',
  'sql',
  'yml',
  'yaml',
  'ini',
]

/**
 * el-upload 的 accept 值：`.<ext>` 列表。
 */
export const PREVIEW_ACCEPT_ATTR = PREVIEW_ACCEPT_EXTENSIONS.map(ext => `.${ext}`).join(',')
